/* eslint-disable max-len */
import JSONSchemaForm from '@rjsf/bootstrap-4';
import { UiSchema } from '@rjsf/core';
import { JSONSchema7 } from 'json-schema';
import React, { Component } from 'react';
import { Dropdown, Tooltip, Button, ButtonGroup } from 'react-bootstrap';
import { connect } from 'react-redux';
import Col from 'react-bootstrap/Col';
import _ from 'lodash';
import { types as repr } from '@sitebuilder/core';
import { types as commonTypes, customSchemas } from '@sitebuilder/common';
import { ThunkDispatch, StateShape as ReduxStateShape } from '../reducers';
import { actionCreators } from '../reducers/templateEditor';
import { actionCreators as compActionCreator } from '../reducers/components';
import { actionCreators as resourcesActionCreators } from '../reducers/resources';
import Widgets from '../components/CustomSchemaWidgets';
import Fields from '../components/CustomSchemaFields';
import RightSideBar from '../components/Layouts/RightSideBar';
import {
    closeIcon,
    iconPdfSvg,
    iconPowerpointSvg,
    iconPreviewSvg,
    iconSaveSvg,
    chevronRightBlack,
    iconMinusPng,
} from '../assets/images';
import { onClickClose as closeColorPicker } from '../components/BaseComponents/ThemeColorPicker';
import ResourcesRightSideBar from '../components/BaseComponents/ResourcesRightSideBar';
import { ObjectFieldTemplate } from '../components/GroupedSchema';
import SaveComponentForm from './SaveComponentForm';
import ResourcePickerLayer from './ResourcePickerLayer';
import AnnotationPickerLayer from './AnnotationPickerLayer';
import DataPickerLayer from './DataPickerLayer';
import FundsPickerLayer from './FundsPickerLayer';

export const MINI_LEFT_SIDEBAR_WIDTH = 54;

const queryWidgets = [
    'Filter With Custom Query',
    'Custom Queries',
    'Dropdown With Custom Query',
    'Download With Graphql Query',
];

interface PropsShape {
    dispatch: ThunkDispatch;
    document: repr.Document | null;
    selectedId: string | null;
    documentName: string | undefined;
    documentType: string;
    editor?: repr.Editor;
    component?: repr.ReferenceComponent | null;
    componentId?: string;
    libraryName?: string;
    isShowResourcePicker?: boolean;
    isShowAnnotationPicker?: boolean;
    isShowDataPicker?: boolean;
    isShowFundsPicker?: boolean;
    selectedSchemaId?: string | null;
    selectedResourcePickerValue?: string | null;
    selectedFundsValue?: string;
    selectedAnnotationPickerValue?: string | null;
    setRightSidebarWidth: (width: number) => void;
    sidebarWidths: { left: number; right: number };
    bottomBarTabs: string[];
    activeSchema?: any;
    showWidgetProperties?: boolean;
    setZoomPercentage?: (percentage: number) => void;
    pageResourcesAction?: string;
    widgetFactory: commonTypes.IWidgetFactory;
    showRightSidebar: boolean;
}

interface StateShape {
    showWidgetProperties: boolean;
    componentData: Record<string, unknown>;
    activeSchema?: JSONSchema7;
    activeUiSchema?: UiSchema;
    type: string;
    zoomPercentage?: string;
    isZoomDDOpen?: boolean;
    selectedTab: string | null;
    additionalSidebarIsOpen: boolean;
}

interface DynamicProperty {
    propertyName: string;
    propertyType?: string;
    propertyBooleanValue?: string;
    propertySingleValue?: string;
    propertyArrayValue?: PropertyArrayValue[];
}

export interface PropertyArrayValue {
    propertyValue?: string;
}

// Genarates dynamic schema for resource strings when user adds new locale
export function generateSchema(locales: string[]) {
    const properties: any = {
        id: {
            type: 'string',
            title: 'Enter Resource ID',
        },
    };

    Array.isArray(locales) &&
        locales.forEach((locale: string) => {
            if (locale) {
                properties[locale as string] = {
                    type: 'string',
                    title: `Enter ${locale.toUpperCase()} Text`,
                };
            }
        });

    return {
        type: 'object',
        properties: properties,
    };
}

// Genarates dynamic UI schema for resource strings when user adds new locale
export function generateUISchema(locales: string[]) {
    const items: any = {
        'ui:field': 'CollapsibleFieldTemplate',
        id: { 'ui:widget': 'CustomTextInput' },
    };

    Array.isArray(locales) &&
        locales.forEach((locale: string) => {
            if (locale) {
                items[locale as string] = { 'ui:widget': 'CustomTextInput' };
            }
        });

    return {
        items,
    };
}

export const generateDynamicUISchema = (dynamicProperties: DynamicProperty[]): JSONSchema7 => {
    // Start by cloning the original schema to avoid direct mutation
    const items: any = {};

    if (dynamicProperties && dynamicProperties.length > 0) {
        dynamicProperties.forEach((dynamicProperty) => {
            const propertyName = dynamicProperty.propertyName;
            const propertyType = dynamicProperty.propertyType || 'string'; // Default to 'string'

            // Handle different types of dynamic properties
            if (propertyType === 'Boolean' && dynamicProperty.propertyBooleanValue) {
                items[propertyName as string] = { 'ui:widget': 'CustomCheckbox' };
            } else if (
                propertyType === 'Multiple values' &&
                dynamicProperty.propertyArrayValue &&
                dynamicProperty.propertyArrayValue.length > 0
            ) {
                items[propertyName as string] = { 'ui:widget': 'CustomSelect' };
            } else if (propertyType === 'Single value' && dynamicProperty.propertySingleValue) {
                items[propertyName as string] = { 'ui:widget': 'CustomTextInput' };
            }
        });
    }

    return items;
};

export const generateDynamicSchema = (dynamicProperties: DynamicProperty[]): JSONSchema7 => {
    // Start by cloning the original schema to avoid direct mutation
    const updatedSchema: JSONSchema7 = {
        type: 'object',
        title: 'Generated Properties',
        properties: {}, // Explicitly initialize properties as an empty object
    };

    if (dynamicProperties && dynamicProperties.length > 0) {
        dynamicProperties.forEach((dynamicProperty) => {
            const propertyName = dynamicProperty.propertyName;
            const propertyType = dynamicProperty.propertyType;

            let propertySchema: JSONSchema7 = {
                type: 'string', // Default to string
                title: propertyName,
            };

            // Handle different types of dynamic properties
            if (propertyType === 'Boolean' && dynamicProperty.propertyName) {
                propertySchema = {
                    type: 'boolean',
                    title: propertyName,
                    default: dynamicProperty.propertyBooleanValue || false,
                };
            } else if (
                propertyType === 'Multiple values' &&
                dynamicProperty.propertyArrayValue &&
                dynamicProperty.propertyArrayValue.length > 1 &&
                dynamicProperty.propertyName
            ) {
                const allPropertyValues: string[] = [];

                dynamicProperty.propertyArrayValue.forEach((property) => {
                    allPropertyValues.push(property.propertyValue || '');
                });

                propertySchema = {
                    type: 'string',
                    title: propertyName,
                    enum: allPropertyValues,
                    default: allPropertyValues[0],
                };
            } else if (
                propertyType === 'Single value' &&
                dynamicProperty.propertySingleValue &&
                dynamicProperty.propertyName
            ) {
                propertySchema = {
                    type: 'string',
                    title: propertyName,
                    default: dynamicProperty.propertySingleValue || '',
                };
            }

            // Safely set the property schema in the 'properties' object
            if (updatedSchema.properties && propertyType) {
                updatedSchema.properties[propertyName] = propertySchema;
            }
        });
    }

    return updatedSchema;
};

// Process resource strings to convert them into an form data
export function processResourceStrings(resourceStrings: any) {
    if (Array.isArray(resourceStrings)) {
        return resourceStrings;
    }

    const resourceMap: any = {};

    // Iterate over each locale object in the input
    for (const lang in resourceStrings) {
        for (const id in resourceStrings[lang]) {
            if (!resourceMap[id]) {
                resourceMap[id] = { id: id };
            }
            // Add the language-specific value to the resource
            resourceMap[id][lang] = resourceStrings[lang][id];
        }
    }

    const result = Object.values(resourceMap);

    return result;
}

export function convertArrayToObject(inputArray: any) {
    const output: any = {};

    inputArray?.forEach((item: any) => {
        const idValue = item.id;
        Object.keys(item).forEach((key) => {
            if (key !== 'id') {
                if (!output[key]) {
                    output[key] = {};
                }
                output[key][idValue] = item[key];
            }
        });
    });

    return output;
}

export function getUniqueIds(text1: string, text2: string) {
    const ids1 = text1 ? text1.split(',') : [];
    const ids2 = text2 ? text2.split(',') : [];
    const set1: any = new Set(ids1);
    const set2: any = new Set(ids2);
    const mergedSet = new Set(Array.from(set1).concat(Array.from(set2)));
    const uniqueIds = Array.from(mergedSet);
    const result = uniqueIds.join(',');

    return result;
}

const CustomArrayFieldTemplate = (props: any) => {
    return (
        <div className="custom-array-field">
            <div>
                <h5>{props.title}</h5>
                <hr className="border-0 bg-secondary" style={{ height: '1px' }}></hr>
            </div>
            {props.items.map((element: any, index: any) => (
                <div key={index} className="array-item">
                    <div className="array-item-content">{element.children}</div>
                    <ButtonGroup className={'btn-array'}>
                        <Button
                            variant="outline-secondary"
                            onClick={element.onReorderClick(index, index - 1)}
                            disabled={!element.hasMoveUp}
                        >
                            <img src={chevronRightBlack} alt={'Up'} className={'up-icon'} />
                        </Button>
                        <Button
                            variant="outline-secondary"
                            onClick={element.onReorderClick(index, index + 1)}
                            disabled={!element.hasMoveDown}
                        >
                            <img src={chevronRightBlack} alt={'Down'} className={'down-icon'} />
                        </Button>
                        <Button variant="outline-danger" onClick={element.onDropIndexClick(index)}>
                            <img src={iconMinusPng} alt={'Delete'} className={'delete-icon'} />
                        </Button>
                    </ButtonGroup>
                </div>
            ))}
            {props.canAdd && (
                <Button variant="outline-primary" onClick={props.onAddClick}>
                    +
                </Button>
            )}
        </div>
    );
};

class Toolbar extends Component<PropsShape, StateShape> {
    editorRef: any;
    formRef: any;
    inputRef: any;
    submitRef: any;
    fixedPercentages: number[];
    observer: MutationObserver | null = null;
    state: StateShape = {
        showWidgetProperties: false,
        componentData: {},
        type: '',
        zoomPercentage: '100%',
        isZoomDDOpen: false,
        selectedTab: null,
        additionalSidebarIsOpen: false,
        activeUiSchema: { 'ui:ObjectFieldTemplate': (props: any) => ObjectFieldTemplate(props) },
    };

    constructor(props: PropsShape) {
        super(props);

        this.editorRef = React.createRef();
        this.formRef = React.createRef();
        this.inputRef = React.createRef();
        this.submitRef = React.createRef();
        this.fixedPercentages = Array.from({ length: 21 }, (_, i) => i * 5);
    }

    handleEditElement = () => {
        const { selectedId, document } = this.props;
        this.onClickClose();

        if (!document?.widgetInstances || !selectedId) {
            this.setState({
                activeSchema: {},
                activeUiSchema: {
                    'ui:ObjectFieldTemplate': (props: any) => ObjectFieldTemplate(props),
                },
                type: '',
                componentData: {},
                showWidgetProperties: false,
            });
            return;
        }

        const type = document?.widgetInstances[selectedId].widgetId;
        let properties: any = document?.widgetInstances[selectedId].properties;
        const refId = document?.widgetInstances?.[selectedId]?.refId;

        if (refId) {
            const refInstance = document?.widgetInstances?.[refId];
            properties = {
                ...properties,
                ...refInstance?.customizations?.[selectedId]?.properties,
            };
        }

        if (properties != null && type) {
            const widget = this.props.widgetFactory?.getWidget(type);
            if (!widget) {
                return;
            }

            let _schema =
                typeof widget?.schema === 'function'
                    ? (widget?.schema as (props: any) => JSONSchema7)(properties)
                    : widget?.schema;
            // let _schema: any =
            //     type === 'document'
            //         ? RootDocument.schema
            //         : typeof EditViewComponents[type].schema === 'function'
            //         ? (EditViewComponents[type].schema as (props: any) => JSONSchema7)(properties)
            //         : EditViewComponents[type].schema;

            if (_schema.title === 'Root Document' && properties?.locales) {
                _schema = {
                    ...widget?.schema,
                    properties: {
                        ...(widget?.schema as JSONSchema7).properties,
                        resourceStrings: {
                            type: 'array',
                            title: 'Add Resource Strings',
                            items: generateSchema(properties?.locales as string[]) as any,
                        },
                    },
                };
            }

            if (_schema.title === 'Schema Builder' || _schema.title === 'Data Context') {
                _schema = {
                    ..._schema,
                    properties: {
                        ..._schema.properties,
                        generatedProperties: generateDynamicSchema(properties.dynamicProperties),
                    },
                };
            }

            let _uiSchema = widget?.uiSchema;
            if (_schema.title === 'Root Document' && properties?.locales) {
                _uiSchema = {
                    ..._uiSchema,
                    resourceStrings: generateUISchema(properties?.locales as string[]) as any,
                };
            } else if (_schema.title === 'Schema Builder' || _schema.title === 'Data Context') {
                _uiSchema = {
                    ..._uiSchema,
                    generatedProperties: generateDynamicUISchema(properties.dynamicProperties),
                };
            }

            this.setState({
                activeSchema: _schema,
                activeUiSchema:
                    type === 'document' || type === 'SchemaBuilder'
                        ? {
                              ..._uiSchema,
                              'ui:ObjectFieldTemplate': (props: any) => ObjectFieldTemplate(props),
                          }
                        : {
                              ...widget.uiSchema,
                              'ui:ObjectFieldTemplate': (props: any) => ObjectFieldTemplate(props),
                          },
                type,
                componentData:
                    _schema.title === 'Root Document'
                        ? {
                              ...properties,
                              resourceStrings: processResourceStrings(properties.resourceStrings),
                          }
                        : properties,
                showWidgetProperties: true,
            });
        }
    };

    handleRemoveElement = () => {
        this.props.dispatch(actionCreators.removeSelected());
    };

    saveUpdatedComponent = (newData: Record<string, unknown>) => {
        const { editor } = this.props;
        if (editor) {
            this.props.dispatch(
                actionCreators.saveWidgetProperties(
                    this.props.selectedId as string,
                    newData,
                    editor
                )
            );
        }
    };

    handleSaveDocument = () => {
        const { dispatch, documentName, editor, componentId, libraryName } = this.props;
        const queryParameters = new URLSearchParams(window.location.search).toString();
        // get the host name and port
        const origin = window.location.origin;

        if (editor === 'pageEditor' && documentName) {
            // get page view url for screenshot generation after page save
            const pageViewUrl = `${origin}/${documentName}${
                queryParameters ? `?${queryParameters}` : ''
            }`;
            dispatch(actionCreators.saveDocument(documentName, pageViewUrl)).then(() =>
                dispatch(actionCreators.loadDocument(documentName)).then(() => {
                    this.elementSelect();
                })
            );
        } else if (editor === 'componentEditor' && libraryName && componentId) {
            // get page view url for screenshot generation after page save
            const compViewUrl = `${origin}/component/${libraryName}/${componentId}${
                queryParameters ? `?${queryParameters}` : ''
            }`;

            this.onClickClose();
            dispatch(compActionCreator.editComponent(libraryName, componentId, compViewUrl)).then(
                () =>
                    dispatch(compActionCreator.loadComponent(libraryName, componentId, true)).then(
                        () => {
                            this.elementSelect();
                        }
                    )
            );
        }
    };

    elementSelect = () => {
        const element = document.getElementsByClassName('selected')[0] as HTMLElement;

        if (element) {
            element.click();
            element.click();
            const appElement = document.querySelector<HTMLElement>('.app');
            const rightSideBarWidth =
                document?.getElementById('right-side-bar-main')?.getBoundingClientRect()?.width ||
                0;
            const elementWidth =
                document?.getElementById('second-left-side-bar')?.getBoundingClientRect()?.width ||
                0;
            appElement &&
                appElement!.style.setProperty(
                    'width',
                    `calc(100% - 25px - ${elementWidth + rightSideBarWidth}px)`
                );
            const toolelement = document.querySelector<HTMLElement>('.right-sidebar-tool');
            toolelement && toolelement!.style.setProperty('width', `${rightSideBarWidth - 10}px`);
        }
    };

    onClickClose = () => {
        const one = document.querySelector<HTMLElement>('.color-picker-selection');
        const moving = document.querySelector<HTMLElement>('.moving')?.nextElementSibling || null;
        const newChildren = one?.children;
        const selectors = document.querySelectorAll<HTMLElement>('.color-picker-selector');
        selectors.forEach((selector) => {
            selector.classList.remove('moving');
        });
        Array.prototype.forEach.call(newChildren, function (child) {
            moving?.append(child);
        });
    };

    closeWidgetProperties = () => {
        this.setState({ showWidgetProperties: false });
    };

    openPageViewer = () => {
        const { documentName, componentId, libraryName } = this.props;
        const queryParameters = new URLSearchParams(window.location.search).toString();

        if (documentName) {
            window.open(
                `/${documentName}${queryParameters ? `?${queryParameters}` : ''}`,
                '_blank'
            );
        } else if (libraryName && componentId) {
            window.open(
                `/component/${libraryName}/${componentId}${
                    queryParameters ? `?${queryParameters}` : ''
                }`,
                '_blank'
            );
        }
    };

    setIsPreview = (isPdf?: boolean, isPptx?: boolean) => {
        const { documentName, componentId, libraryName } = this.props;
        const queryParameters = new URLSearchParams(window.location.search).toString();
        let url = '';
        let documentData;

        if (documentName) {
            url = `/preview/${documentName}${queryParameters ? `?${queryParameters}` : ''}`;
            documentData = this.props.document;
        } else if (libraryName && componentId) {
            url = `/preview/component/${libraryName}/${componentId}${
                queryParameters ? `?${queryParameters}` : ''
            }`;
            documentData = this.props.component;
        }

        if (typeof document !== 'undefined') {
            const form = document.createElement('form');
            form.setAttribute('method', 'post');
            form.setAttribute('action', url);
            form.setAttribute('target', 'newtab');
            const hiddenField = document.createElement('input');
            hiddenField.setAttribute('type', 'hidden');
            hiddenField.setAttribute('name', 'document');
            hiddenField.setAttribute('value', `${JSON.stringify(documentData)}`);
            const hiddenField1 = document.createElement('input');
            hiddenField1.setAttribute('type', 'hidden');

            if (isPdf) {
                hiddenField1.setAttribute('name', 'isPdf');
                hiddenField1.setAttribute('value', `${isPdf}`);
            }

            if (isPptx) {
                hiddenField1.setAttribute('name', 'isPptx');
                hiddenField1.setAttribute('value', `${isPptx}`);
            }

            form?.appendChild(hiddenField);
            form?.appendChild(hiddenField1);
            document.body.appendChild(form);

            form.submit();
        }
    };

    componentDidUpdate(prevProps: PropsShape) {
        const {
            dispatch,
            selectedId,
            isShowDataPicker,
            isShowAnnotationPicker,
            isShowResourcePicker,
            selectedResourcePickerValue,
            selectedAnnotationPickerValue,
            selectedFundsValue,
            selectedSchemaId,
            bottomBarTabs,
        } = this.props;
        const schema = this.state.activeSchema;
        const isQueryWidget = schema?.title && queryWidgets.includes(schema?.title);

        if (prevProps.selectedId !== selectedId) {
            this.handleEditElement();
        }

        if (
            prevProps.selectedResourcePickerValue !== selectedResourcePickerValue ||
            prevProps.selectedAnnotationPickerValue !== selectedAnnotationPickerValue ||
            prevProps.selectedFundsValue !== selectedFundsValue
        ) {
            if (selectedSchemaId) {
                let schemaId = selectedSchemaId;
                [0, 1, 2, 3, 4, 5, 6, 7, 8, 9].forEach((d: any) => {
                    schemaId = schemaId?.replace(new RegExp(`_${d}_`, 'g'), `[${d}]_`);
                });

                schemaId = schemaId.replace('root_', '').replaceAll('_', '.');
                const data = _.cloneDeep(this.state.componentData);

                const currentValue: any = _.get(data, schemaId, '');
                let value: any = '';
                let showOnlyPresentVal = false;

                if (prevProps.selectedResourcePickerValue !== selectedResourcePickerValue) {
                    value = selectedResourcePickerValue;
                } else if (
                    prevProps.selectedAnnotationPickerValue !== selectedAnnotationPickerValue
                ) {
                    value = selectedAnnotationPickerValue;
                } else if (prevProps.selectedFundsValue !== selectedFundsValue) {
                    value = getUniqueIds(currentValue as string, selectedFundsValue as string);
                    showOnlyPresentVal = true;
                }

                this.setState({
                    ...this.state,
                    componentData: _.update(data, schemaId, () =>
                        showOnlyPresentVal ? value : `${currentValue} ${value}`
                    ),
                });

                if (!isQueryWidget) {
                    setTimeout(() => {
                        this.submitRef?.current?.click();
                    }, 300);
                }
                dispatch(resourcesActionCreators.setselectedSchemaId(null));
                isShowResourcePicker &&
                    dispatch(resourcesActionCreators.setIsShowResourcePicker(false));
                isShowAnnotationPicker && dispatch(actionCreators.setIsShowAnnotationPicker(false));
            }
        }

        if (
            (!prevProps.isShowDataPicker && isShowDataPicker) ||
            (!prevProps.isShowAnnotationPicker && isShowAnnotationPicker) ||
            (!prevProps.isShowResourcePicker && isShowResourcePicker) ||
            (!prevProps.pageResourcesAction && this.props.pageResourcesAction)
        ) {
            document.getElementById('right-side-bar-main')?.style.setProperty('max-width', '700px');
            const rigthSidebarElem = document.querySelector<HTMLElement>('.right-sidebar-tool');
            const dataPickerElem = document.querySelector<HTMLElement>('.data-picker');
            rigthSidebarElem?.style?.setProperty('width', '100%');
            document.getElementById('right-side-bar-main')?.style.setProperty('width', '550px');
            const rjsfElem = document.querySelector<HTMLElement>('.rjsf');
            const combineWidth = (dataPickerElem?.offsetWidth || 0) + (rjsfElem?.offsetWidth || 0);
            if (combineWidth > 550) {
                rigthSidebarElem?.style?.setProperty('width', `${combineWidth}px`);
            }
        }

        if (prevProps.bottomBarTabs !== bottomBarTabs) {
            let newTab = '';

            bottomBarTabs.forEach((t) => {
                if (!prevProps.bottomBarTabs.includes(t)) {
                    newTab = t;
                }
            });

            if (!newTab && bottomBarTabs.length > 0) {
                newTab = bottomBarTabs[0];
            }

            this.setState({ selectedTab: newTab });
        }

        if (
            prevProps.document?.widgetInstances.root?.properties?.resourceStrings !==
                this.props.document?.widgetInstances.root?.properties?.resourceStrings ||
            prevProps.document?.widgetInstances.root?.properties?.locales !==
                this.props.document?.widgetInstances.root?.properties?.locales
        ) {
            const resourceStrings =
                this.props.document?.widgetInstances.root?.properties?.resourceStrings;
            const locales = this.props.document?.widgetInstances.root?.properties?.locales;
            const data = _.cloneDeep(this.state.componentData);
            this.setState({
                ...this.state,
                componentData: {
                    ...data,
                    resourceStrings: processResourceStrings(resourceStrings),
                    locales: locales,
                },
            });
        }
    }

    handleMouseWheel = (e: WheelEvent) => {
        const delta = e.deltaY || e.detail;
        const isZoom = e.ctrlKey || e.metaKey;
        if (!isZoom) return;

        e.preventDefault();

        const zoomFactor = 0.1;
        const zoomIn = delta < 0;
        const newZoom = zoomIn ? 1 + zoomFactor : 1 - zoomFactor;

        let newZoomPercentage = Number(this.state.zoomPercentage!.replace('%', '')) * newZoom;
        // Ensure the zoom percentage is within desired limits
        newZoomPercentage = Math.round(Math.max(10, Math.min(999, newZoomPercentage)));
        this.handleZoomChange(newZoomPercentage);
    };

    componentDidMount() {
        document.addEventListener('wheel', this.handleMouseWheel, { passive: false });

        const colorPickerSidebar = document.querySelector('.color-picker-sidebar');

        if (colorPickerSidebar) {
            this.observer = new MutationObserver(() =>
                this.setState({ additionalSidebarIsOpen: !this.state.additionalSidebarIsOpen })
            );

            this.observer.observe(colorPickerSidebar, { childList: true, subtree: true });
        }
    }

    componentWillUnmount() {
        document.removeEventListener('wheel', this.handleMouseWheel);

        if (this.observer) {
            this.observer.disconnect();
        }
    }

    handleZoomChange = (percentage: number) => {
        this.setState({ zoomPercentage: `${percentage}%` });
        this.props.setZoomPercentage?.(percentage);
    };

    handleInputClick = () => {
        if (this.inputRef.current) {
            const position = this.inputRef.current.value.lastIndexOf('%');
            if (position !== -1) {
                this.inputRef.current.setSelectionRange(position, position);
            }
        }
    };

    renderButtons = (
        isQueryWidget: boolean,
        isShowResourcePicker: boolean | undefined,
        alwaysHide?: boolean
    ) => {
        const { dispatch } = this.props;
        return (
            <div
                className={`action-buttons ${
                    isQueryWidget && !alwaysHide ? 'query-widget-action-buttons' : ''
                }`}
            >
                <div className="buttons-container">
                    <Button
                        ref={this.submitRef}
                        className="submit-button"
                        variant="secondary"
                        type="submit"
                        onClick={() => {
                            this.formRef?.current?.submit();
                        }}
                    >
                        Submit
                    </Button>
                    <Button
                        variant="secondary"
                        type="cancel"
                        onClick={() => {
                            closeColorPicker();
                            this.closeWidgetProperties();
                            isShowResourcePicker &&
                                dispatch(
                                    // eslint-disable-next-line max-len
                                    resourcesActionCreators.setIsShowResourcePicker(false)
                                );
                            dispatch(actionCreators.setIsShowDataPicker(false));
                            dispatch(actionCreators.setIsShowAnnotationPicker(false));
                        }}
                    >
                        Cancel
                    </Button>
                </div>
            </div>
        );
    };

    render(): JSX.Element {
        const {
            dispatch,
            selectedId,
            setRightSidebarWidth,
            sidebarWidths,
            bottomBarTabs,
            isShowResourcePicker,
            activeSchema,
            showWidgetProperties,
            showRightSidebar,
        } = this.props;
        const schema = this.state.activeSchema || activeSchema;
        const uiSchema = this.state.activeUiSchema;
        const initialRightSideBarWidth =
            document.getElementById('right-side-bar-main')?.getBoundingClientRect().width || 0;
        const isQueryWidget = schema?.title && queryWidgets.includes(schema?.title);
        const widgetInstance = selectedId
            ? this.props.document?.widgetInstances?.[selectedId]
            : null;

        const showWidgetEditor =
            (this.state.showWidgetProperties || showWidgetProperties) &&
            (showRightSidebar || widgetInstance?.id === 'root');

        return (
            <>
                <div className="toolbar">
                    <Button className="toolbar-button" type="submit" onClick={this.openPageViewer}>
                        View
                    </Button>
                    <span className="zoom-setting">
                        <Dropdown
                            onSelect={(eventKey) => this.handleZoomChange(parseInt(eventKey!) || 0)}
                            className="zoom-dropdown"
                            onToggle={(isOpen: boolean) => this.setState({ isZoomDDOpen: isOpen })}
                            data-testid="zoom-dropdown"
                        >
                            <Dropdown.Toggle variant="secondary" id="zoom-dropdown">
                                <input
                                    type="text"
                                    value={this.state.zoomPercentage}
                                    ref={this.inputRef}
                                    onClick={this.handleInputClick}
                                    className="zoom-input"
                                    data-testid="zoom-input"
                                    onChange={(e) =>
                                        this.handleZoomChange(parseInt(e.target.value) || 0)
                                    }
                                    maxLength={4}
                                />
                                <i
                                    className={`dropdown-toggle-icon fas ${
                                        this.state.isZoomDDOpen
                                            ? 'fa-chevron-up'
                                            : 'fa-chevron-down'
                                    }`}
                                />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {this.fixedPercentages.map((percentage, index) => (
                                    <Dropdown.Item key={index} eventKey={percentage}>
                                        {`${percentage}%`}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    </span>
                    {this.props.documentType === 'pdf' && (
                        <Tooltip
                            id={'preview-pdf'}
                            placement="right"
                            title={'Preview PDF'}
                            color={'#B2BEB5'}
                        >
                            <img
                                className="preview-pdf-icon"
                                src={iconPdfSvg}
                                alt="preview-pdf-icon-svg"
                                onClick={() => this.setIsPreview(true)}
                            />
                        </Tooltip>
                    )}
                    {this.props.documentType === 'pptx' && (
                        <Tooltip
                            id={'preview-ppt'}
                            placement="right"
                            title={'Preview PPTX'}
                            color={'#B2BEB5'}
                        >
                            <img
                                className="preview-ppt-icon"
                                src={iconPowerpointSvg}
                                alt="preview-pptx-icon-svg"
                                onClick={() => this.setIsPreview(false, true)}
                            />
                        </Tooltip>
                    )}
                    <Tooltip id={'preview'} placement="right" title={'Preview'} color={'#B2BEB5'}>
                        <img
                            className="preview-icon"
                            src={iconPreviewSvg}
                            alt="preview-pptx-icon-svg"
                            onClick={() => this.setIsPreview(false)}
                        />
                    </Tooltip>
                    <Button
                        className={'save-button toolbar-button'}
                        type="button"
                        onClick={this.handleSaveDocument}
                    >
                        <img className="save-icon" src={iconSaveSvg} alt="icom-save-svg" />
                        <span>Save</span>
                    </Button>
                </div>
                <Col>
                    <RightSideBar
                        setRightSidebarWidth={setRightSidebarWidth}
                        hide={!showWidgetEditor}
                        withAdditionalWidth={
                            this.props.isShowResourcePicker ||
                            this.props.isShowAnnotationPicker ||
                            this.props.isShowDataPicker ||
                            !!this.props.pageResourcesAction ||
                            this.props.isShowFundsPicker ||
                            this.state.additionalSidebarIsOpen
                        }
                    >
                        <div className="right-sidebar-tool" data-testid="right-sidebar-tool">
                            <div className="container">
                                <div className="color-picker-selection"></div>
                                <div className="color-picker-sidebar"></div>
                                {this.props.isShowResourcePicker && (
                                    <div className="resource-picker">
                                        <ResourcePickerLayer />
                                    </div>
                                )}
                                {this.props.isShowAnnotationPicker && (
                                    <div className="annotation-picker">
                                        <AnnotationPickerLayer />
                                    </div>
                                )}
                                {this.props.isShowDataPicker && (
                                    <div className="data-picker">
                                        <DataPickerLayer />
                                    </div>
                                )}
                                {this.props.isShowFundsPicker && (
                                    <div className="funds-picker">
                                        <FundsPickerLayer />
                                    </div>
                                )}
                                {this.props.pageResourcesAction && (
                                    <div className="data-picker">
                                        <ResourcesRightSideBar
                                            resourcesType={'page'}
                                            isShowCloseIcon={true}
                                            addNewLocale={
                                                this.props.pageResourcesAction === 'locale'
                                            }
                                            resources={null}
                                            filteredResources={
                                                convertArrayToObject(
                                                    this.state.componentData.resourceStrings
                                                ) as repr.Resource
                                            }
                                            addNewResource={
                                                this.props.pageResourcesAction === 'resource'
                                            }
                                        />
                                    </div>
                                )}
                                <div className="editor" ref={this.editorRef}>
                                    {showWidgetEditor && (
                                        <>
                                            {schema && (
                                                <JSONSchemaForm
                                                    ref={this.formRef}
                                                    className={`rjsf ${
                                                        isQueryWidget
                                                            ? 'rjsf-query-widget-form'
                                                            : ''
                                                    }`}
                                                    ArrayFieldTemplate={CustomArrayFieldTemplate}
                                                    data-testid="rjsf-form"
                                                    schema={{
                                                        ...(this.state.activeSchema ?? {}),
                                                        properties: {
                                                            ...this.state.activeSchema?.properties,
                                                            ...customSchemas.ComponentInfoSchema
                                                                .ComponentInfoSchema.schema,
                                                        },
                                                    }}
                                                    uiSchema={{
                                                        ...(this.state.activeUiSchema ?? {}),
                                                        ...customSchemas.ComponentInfoSchema
                                                            .ComponentInfoSchema.uiSchema,
                                                        'ui:groups': [
                                                            {
                                                                ...(this.state.activeUiSchema?.[
                                                                    'ui:groups'
                                                                ]?.[0] || {}),
                                                                ...customSchemas.ComponentInfoSchema
                                                                    .ComponentInfoSchema.group,
                                                            },
                                                        ],
                                                    }}
                                                    widgets={Widgets}
                                                    fields={Fields}
                                                    formData={this.state.componentData}
                                                    formContext={{
                                                        rootFormData: this.state.componentData,
                                                        editorRef: this.editorRef?.current,
                                                    }}
                                                    onChange={({ formData }: any) => {
                                                        const type = this.state.type;
                                                        const widget =
                                                            this.props.widgetFactory?.getWidget(
                                                                type
                                                            );

                                                        if (!widget) {
                                                            return;
                                                        }

                                                        let _schema =
                                                            typeof widget?.schema === 'function'
                                                                ? (
                                                                      widget?.schema as (
                                                                          props: any
                                                                      ) => JSONSchema7
                                                                  )(formData)
                                                                : widget?.schema;
                                                        // this.state.type === 'document'
                                                        //     ? RootDocument.schema
                                                        //     : typeof EditViewComponents[type]
                                                        //           .schema === 'function'
                                                        //     ? (
                                                        //           EditViewComponents[type]
                                                        //               .schema as (
                                                        //               props: any
                                                        //           ) => JSONSchema7
                                                        //       )(formData)
                                                        //     : EditViewComponents[type].schema;

                                                        let _uiSchema = {
                                                            ...this.state.activeUiSchema,
                                                        };
                                                        if (
                                                            _schema.title === 'Root Document' &&
                                                            formData?.locales
                                                        ) {
                                                            _schema = {
                                                                ...this.state.activeSchema,
                                                                properties: {
                                                                    ...this.state.activeSchema
                                                                        ?.properties,
                                                                    resourceStrings: {
                                                                        type: 'array',
                                                                        title: 'Add Resource Strings',
                                                                        items: generateSchema(
                                                                            formData?.locales as string[]
                                                                        ) as any,
                                                                    },
                                                                },
                                                            };
                                                        }
                                                        if (
                                                            _schema.title === 'Root Document' &&
                                                            formData?.locales
                                                        ) {
                                                            _uiSchema = {
                                                                ...this.state.activeUiSchema,
                                                                resourceStrings: generateUISchema(
                                                                    formData?.locales as string[]
                                                                ) as any,
                                                            };
                                                        }
                                                        if (
                                                            _schema.title === 'Schema Builder' ||
                                                            _schema.title === 'Data Context'
                                                        ) {
                                                            _schema = {
                                                                ...this.state.activeSchema,
                                                                properties: {
                                                                    ...this.state.activeSchema
                                                                        ?.properties,
                                                                    generatedProperties:
                                                                        generateDynamicSchema(
                                                                            formData.dynamicProperties
                                                                        ),
                                                                },
                                                            };
                                                        }
                                                        if (
                                                            _schema.title === 'Schema Builder' ||
                                                            _schema.title === 'Data Context'
                                                        ) {
                                                            _uiSchema = {
                                                                ...this.state.activeUiSchema,
                                                                generatedProperties:
                                                                    generateDynamicUISchema(
                                                                        formData.dynamicProperties
                                                                    ),
                                                            };
                                                        }

                                                        this.setState({
                                                            ...this.state,
                                                            activeSchema: _schema,
                                                            activeUiSchema: _uiSchema,
                                                            showWidgetProperties: true,
                                                            componentData: formData,
                                                        });
                                                        if (!isQueryWidget) {
                                                            setTimeout(() => {
                                                                this.saveUpdatedComponent(formData);
                                                            }, 300);
                                                        }
                                                    }}
                                                    onSubmit={(e: any) => {
                                                        this.saveUpdatedComponent(e.formData);
                                                        this.onClickClose();
                                                        isShowResourcePicker &&
                                                            dispatch(
                                                                // eslint-disable-next-line max-len
                                                                resourcesActionCreators.setIsShowResourcePicker(
                                                                    false
                                                                )
                                                            );
                                                        dispatch(
                                                            actionCreators.setIsShowAnnotationPicker(
                                                                false
                                                            )
                                                        );
                                                    }}
                                                    onError={(e: any) => console.log(e)}
                                                >
                                                    {this.renderButtons(
                                                        isQueryWidget,
                                                        isShowResourcePicker
                                                    )}
                                                </JSONSchemaForm>
                                            )}
                                            <SaveComponentForm />
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </RightSideBar>
                </Col>

                {bottomBarTabs?.length && !!showWidgetEditor ? (
                    <div
                        className="bottom-bar"
                        style={{
                            left: `${sidebarWidths?.left + MINI_LEFT_SIDEBAR_WIDTH}px`,
                            width: `${
                                window.screen.availWidth -
                                (sidebarWidths?.right || initialRightSideBarWidth) -
                                sidebarWidths?.left -
                                MINI_LEFT_SIDEBAR_WIDTH -
                                25
                            }px`,
                        }}
                    >
                        <div className="bottom-bar-tabs-container">
                            {bottomBarTabs?.map((t, i) => (
                                <div
                                    key={`bottom-bar-tab-${i}`}
                                    className={`tab ${
                                        t === this.state.selectedTab ? 'active' : ''
                                    }`}
                                    onClick={() => this.setState({ selectedTab: t })}
                                >
                                    {t}

                                    <img
                                        className="close-icon"
                                        src={closeIcon}
                                        alt="close-icon"
                                        onClick={(e: React.MouseEvent<HTMLElement>) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            dispatch(actionCreators.closeBottomBarTab(t));
                                        }}
                                    />
                                </div>
                            ))}
                        </div>

                        <img
                            className="close-all-icon"
                            src={closeIcon}
                            alt="close-all-icon"
                            onClick={(e: React.MouseEvent<HTMLElement>) => {
                                e.preventDefault();
                                e.stopPropagation();
                                dispatch(actionCreators.closeAllBottomBarTabs());
                            }}
                        />

                        {bottomBarTabs?.map((t, i) => (
                            <div
                                key={`bottom-bar-content-${i}`}
                                className={`editor bottom-bar-content ${
                                    t === this.state.selectedTab ? '' : 'hidden'
                                }`}
                                ref={this.editorRef}
                            >
                                {this.state.showWidgetProperties && (
                                    <>
                                        {schema && (
                                            <JSONSchemaForm
                                                schema={schema}
                                                uiSchema={uiSchema}
                                                ArrayFieldTemplate={CustomArrayFieldTemplate}
                                                widgets={Widgets}
                                                fields={Fields}
                                                formData={this.state.componentData}
                                                formContext={{
                                                    rootFormData: this.state.componentData,
                                                    editorRef: this.editorRef?.current,
                                                    bottomBar: true,
                                                    bottomBarKey: t,
                                                }}
                                                onChange={({ formData }: any) => {
                                                    this.setState({
                                                        ...this.state,
                                                        showWidgetProperties: true,
                                                        componentData: formData,
                                                    });
                                                    if (!isQueryWidget) {
                                                        setTimeout(() => {
                                                            this.saveUpdatedComponent(formData);
                                                        }, 300);
                                                    }
                                                }}
                                                onSubmit={(e: any) => {
                                                    this.saveUpdatedComponent(e.formData);
                                                    this.onClickClose();
                                                    isShowResourcePicker &&
                                                        dispatch(
                                                            // eslint-disable-next-line max-len
                                                            resourcesActionCreators.setIsShowResourcePicker(
                                                                false
                                                            )
                                                        );
                                                    dispatch(
                                                        actionCreators.setIsShowAnnotationPicker(
                                                            false
                                                        )
                                                    );
                                                }}
                                            >
                                                {this.renderButtons(
                                                    isQueryWidget,
                                                    isShowResourcePicker,
                                                    true
                                                )}
                                            </JSONSchemaForm>
                                        )}
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <></>
                )}
            </>
        );
    }
}

const mapStateToProps = (state: ReduxStateShape) => ({
    ...state.components,
    ...state.templateEditor,
    ...state.app,
    ...state.resources,
    ...state.funds,
});

export default connect(mapStateToProps)(Toolbar);
